import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { userApi } from '../services/userService';

// 创建认证上下文
const AuthContext = createContext(null);

// 认证上下文提供者组件
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasCheckedAuthRef = useRef(false);

  // 初始化时检查用户是否已登录
  useEffect(() => {
    if (hasCheckedAuthRef.current) {
      return;
    }
    hasCheckedAuthRef.current = true;

    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const userData = await userApi.getCurrentUser();
          setUser(userData);
        } catch (err) {
          console.error('获取用户信息失败:', err);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // 刷新用户信息
  const refreshUserInfo = async () => {
    try {
      const userData = await userApi.getCurrentUser();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (err) {
      console.error('刷新用户信息失败:', err);
      throw err;
    }
  };

  // 登录
  const login = async (username, password) => {
    setLoading(true);
    setError(null);
    try {
      // 登录请求
      const loginResponse = await userApi.login(username, password);
      
      if (!loginResponse || !loginResponse.access_token) {
        throw new Error('登录响应中没有访问令牌');
      }
      
      // 等待一小段时间确保token已被保存
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // 验证token是否正确保存
      const savedToken = localStorage.getItem('token');
      
      if (!savedToken) {
        throw new Error('Token未能正确保存');
      }
      
      // 获取用户信息
      const userData = await userApi.getCurrentUser();
      
      if (!userData) {
        throw new Error('无法获取用户信息');
      }
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      
      return userData;
    } catch (err) {
      console.error('登录失败:', err);
      
      // 清理token和用户信息
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // 设置错误信息
      const errorMessage = err.response?.data?.detail || err.message || '登录失败，请检查用户名和密码';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 登出
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // 更新用户信息
  const updateUserInfo = async (data) => {
    try {
      const updatedUser = await userApi.updateProfile(data);
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (err) {
      throw err;
    }
  };

  // 修改密码
  const changePassword = async (oldPassword, newPassword) => {
    try {
      await userApi.changePassword({
        old_password: oldPassword,
        new_password: newPassword
      });
      return true;
    } catch (err) {
      throw err;
    }
  };

  // 提供的上下文值
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    updateUserInfo,
    changePassword,
    isAdmin: user?.user_group === 'admin',
    refreshUserInfo
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// 自定义钩子，用于访问认证上下文
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth必须在AuthProvider内部使用');
  }
  return context;
};

export default AuthContext; 